import type { PlainEnvironment } from "@/queries/environment-queries";
import type { EffectiveState } from "@/routes/environments/phase";
import type { PlainMessage } from "@bufbuild/protobuf";
import { EnvironmentPhase, type EnvironmentInitializer } from "gitpod-next-api/gitpod/v1/environment_pb";

export function statusText(state?: EffectiveState): string | undefined {
    if (!state) {
        return;
    }

    let text = "Unknown";

    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            text = "Running";
            break;
        case EnvironmentPhase.CREATING:
            text = "Starting";
            break;
        case EnvironmentPhase.STARTING:
            if (state.resuming) {
                text = "Resuming";
            } else {
                text = "Starting";
            }
            break;
        case EnvironmentPhase.UPDATING:
            text = "Updating";
            break;
        case EnvironmentPhase.STOPPING:
            text = "Stopping";
            if (state.timeout) {
                text = `Auto-stopping`;
            }
            break;
        case EnvironmentPhase.STOPPED:
            text = "Stopped";
            if (state.timeout) {
                text = `Auto-stopped`;
            }
            break;
        case EnvironmentPhase.DELETING:
            text = "Deleting";
            break;
        case EnvironmentPhase.DELETED:
            text = "Deleted";
            break;
    }

    return text;
}

export function environmentLogsUrl(environment?: PlainEnvironment) {
    if (!environment?.status?.environmentUrls?.logs) {
        return;
    }
    // We're adding the status machine session to the URL to trigger a refresh when a new machine session starts
    // The macOS runner resets the log stream, so this ensures we clear our logs as well.
    return `${environment?.status?.environmentUrls?.logs}#${environment?.status?.machine?.session}`;
}

export function environmentLogGroupUrl(environmentId?: string, logGroupId?: string) {
    return `/details/${environmentId}/logs${logGroupId ? `#${logGroupId}` : ""}`;
}

/**
 * Extracts the git branch from the environment initializer.
 *
 * The environment initializer can contain multiple initialization specs, each with a different type of context.
 * This function searches through the specs and returns the git branch from the first spec that has a "contextUrl" or "git" case.
 *
 * @param initializer The environment initializer object.
 * @returns The repository URL, or undefined if no URL could be found.
 */
export function getBranchFromInitializer(initializer: PlainMessage<EnvironmentInitializer> | undefined) {
    if (!initializer?.specs || initializer.specs.length === 0) {
        return "main"; // default branch for empty initializer
    }
    const urls = initializer?.specs
        .map((s) => {
            switch (s.spec.case) {
                case "contextUrl":
                    return extractBranchFromUrl(s.spec.value.url);
                case "git":
                    return s.spec.value.cloneTarget;
            }
        })
        .filter((u) => !!u)
        .map((u) => u!);
    return urls?.[0];
}

export function extractBranchFromUrl(url: string) {
    // This works for both GitHub and GitLab URLs
    // Might need to be adjusted for future SCM providers
    const parts = url.split("/tree/");

    // We're defaulting to "default branch" here instead of "main" as we can't know what the default branch is for the repository.
    if (parts.length === 1) {
        return "default branch";
    }

    const branch = parts.pop()?.replace(/\?.*/, "");
    return branch || url;
}
